/**
 * RecastNavigationServer - Recast navigation integration for AI pathfinding.
 * Wraps navmesh generation and query APIs for actor path requests.
 */

import {
  allocCompactHeightfield,
  allocContourSet,
  allocHeightfield,
  allocPolyMesh,
  allocPolyMeshDetail,
  buildCompactHeightfield,
  buildContours,
  buildDistanceField,
  buildPolyMesh,
  buildPolyMeshDetail,
  buildRegions,
  calcGridSize,
  createHeightfield,
  createNavMeshData,
  createRcConfig,
  erodeWalkableArea,
  filterLedgeSpans,
  filterLowHangingWalkableObstacles,
  filterWalkableLowHeightSpans,
  freeCompactHeightfield,
  freeContourSet,
  freeHeightfield,
  freePolyMesh,
  freePolyMeshDetail,
  markWalkableTriangles,
  NavMeshCreateParams,
  rasterizeTriangles,
  Recast,
  RecastBuildContext,
  TriangleAreasArray,
  TrianglesArray,
  VerticesArray,
} from '@recast-navigation/core';
import { DebugDrawer } from '@recast-navigation/three';
import { exportNavMesh, importNavMesh, init, NavMesh, NavMeshQuery } from 'recast-navigation';
import { generateSoloNavMesh, getBoundingBox, mergePositionsAndIndices } from 'recast-navigation/generators';
import * as THREE from 'three';
import { deinterleaveGeometry } from 'three/addons/utils/BufferGeometryUtils.js';

import { NAVMESH_EXT } from '../const.js';
import { projectInfo } from '../storage/projectContext.js';
import { AssetPath } from '../storage/storageProvider.js';
import { ObjectTracker } from '../systems/ObjectTracker.js';

import type { INavigationServer, NavigationMeshGenerationInput, NavigationOptions } from './NavigationServer.js';
import type { Mesh } from 'three';

// Recast Navigation Debug Drawer uses LineSegments2 which is not supported by WebGPU
function replaceDebugDrawerLinesWithThreeEdges(debugDrawer: THREE.Group): void {
  for (const child of [...debugDrawer.children]) {
    if (child.type !== 'LineSegments2') {
      continue;
    }
    debugDrawer.remove(child);
    const lineChild = child as THREE.Object3D & { geometry?: THREE.BufferGeometry };
    lineChild.geometry?.dispose();
  }

  const edgeMaterial = new THREE.LineBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 1,
    depthWrite: false,
  });

  debugDrawer.traverse(child => {
    if (!(child instanceof THREE.Mesh)) {
      return;
    }
    const edgeGeometry = new THREE.EdgesGeometry(child.geometry);
    const positionAttribute = edgeGeometry.getAttribute('position');
    if (!positionAttribute || positionAttribute.count === 0) {
      edgeGeometry.dispose();
      return;
    }

    const edgeLines = new THREE.LineSegments(edgeGeometry, edgeMaterial.clone());
    edgeLines.renderOrder = child.renderOrder + 1;
    child.add(edgeLines);
  });
}


export class RecastNavigationServer implements INavigationServer {
  // Set from NavigationOptions
  private options: any;

  // Runtime properties
  private navigationMesh: NavMesh | null = null;
  private navMeshQuery: NavMeshQuery | null = null;

  constructor() {
    ObjectTracker.track(this);
  }

  // TODO: use a proper type for this
  static get DEFAULT_OPTIONS(): any {
    return {
      borderSize: 0,
      cs: 1,
      ch: 0.2,
      walkableSlopeAngle: 35,
      walkableHeight: 1,
      walkableClimb: 1,
      walkableRadius: 1,
      maxEdgeLen: 12,
      maxSimplificationError: 1.3,
      minRegionArea: 8,
      mergeRegionArea: 20,
      maxVertsPerPoly: 6,
      detailSampleDist: 6,
      detailSampleMaxError: 1,
    };
  }

  initialize(options: NavigationOptions): Promise<boolean> {
    const incoming = options.options;
    this.options =
      incoming !== null && typeof incoming === 'object'
        ? { ...RecastNavigationServer.DEFAULT_OPTIONS, ...incoming }
        : { ...RecastNavigationServer.DEFAULT_OPTIONS };
    return init().then(() => true);
  }

  updateOptions(partial: Record<string, unknown>): void {
    this.options = { ...this.options, ...partial };
  }
  destroy(): void {
    // Properly dispose of WASM objects to free memory
    if (this.navMeshQuery) {
      this.navMeshQuery.destroy();
      this.navMeshQuery = null;
    }
    if (this.navigationMesh) {
      this.navigationMesh.destroy();
      this.navigationMesh = null;
    }
  }

  createNavigationMesh(mesh: Mesh): Promise<boolean> {
    const positions = mesh.geometry.attributes.position.array;
    const indices = mesh.geometry.index?.array;

    if (!positions) {
      console.error('[RecastNavigationServer] createNavigationMesh failed: Positions could not be determined from mesh.');
      return Promise.resolve(false);
    }

    if (!indices) {
      console.error('[RecastNavigationServer] createNavigationMesh failed: Indices could not be determined from mesh.');
      return Promise.resolve(false);
    }

    const result = generateSoloNavMesh(positions!, indices!, this.options);
    this.setNavigationMesh(result.navMesh!);

    return Promise.resolve(true);
  }


  createNavigationMeshFromMeshes(input: NavigationMeshGenerationInput, overrideOptions?: any): Promise<boolean> {
    const walkableMeshes = input.walkableMeshes ?? [];
    const obstacleMeshes = input.obstacleMeshes ?? [];
    const totalMeshCount = walkableMeshes.length + obstacleMeshes.length;

    console.log(
      `[RecastNavigationServer] Creating navigation mesh from ${totalMeshCount} meshes (${walkableMeshes.length} walkable, ${obstacleMeshes.length} obstacle) ...`
    );

    if (totalMeshCount === 0) {
      console.warn('[RecastNavigationServer] No meshes provided for navigation mesh generation');
      return Promise.resolve(false);
    }

    try {
      // Deinterleave meshes before processing to ensure proper geometry formatting
      const deinterleavedWalkableMeshes = this.deinterleaveMeshes(walkableMeshes);
      const deinterleavedObstacleMeshes = this.deinterleaveMeshes(obstacleMeshes);
      const result = this.createSoloNavMeshFromClassifiedMeshes(
        deinterleavedWalkableMeshes,
        deinterleavedObstacleMeshes,
        overrideOptions ?? this.options,
      );

      if (result.success) {
        this.setNavigationMesh(result.navMesh!);
        console.log('[RecastNavigationServer] Navigation mesh created successfully');
      }
      else {
        const errorMessage = result.error ?? 'Unknown error during navigation mesh generation';
        console.error('[RecastNavigationServer] role-aware navmesh generation failed:', errorMessage);
      }

      return Promise.resolve(!!result.success);
    }
    catch (error) {
      console.error('[RecastNavigationServer] Runtime error while creating navigation mesh:', error);
      return Promise.resolve(false);
    }
  }

  private extractWorldPositionsAndIndices(meshes: THREE.Mesh[]): { positions: Float32Array; indices: Uint32Array } {
    const toMerge: { positions: ArrayLike<number>; indices: ArrayLike<number> }[] = [];
    const tempPosition = new THREE.Vector3();

    for (const mesh of meshes) {
      const positionAttribute = mesh.geometry.attributes.position as THREE.BufferAttribute | undefined;
      if (!positionAttribute || positionAttribute.itemSize !== 3) {
        continue;
      }

      mesh.updateWorldMatrix(true, false);
      const positions = new Float32Array(positionAttribute.array as ArrayLike<number>);

      for (let i = 0; i < positions.length; i += 3) {
        tempPosition.set(positions[i], positions[i + 1], positions[i + 2]);
        mesh.localToWorld(tempPosition);
        positions[i] = tempPosition.x;
        positions[i + 1] = tempPosition.y;
        positions[i + 2] = tempPosition.z;
      }

      let indices = mesh.geometry.getIndex()?.array as ArrayLike<number> | undefined;
      if (indices === undefined) {
        const ascending = new Uint32Array(positionAttribute.count);
        for (let i = 0; i < positionAttribute.count; i++) {
          ascending[i] = i;
        }
        indices = ascending;
      }

      toMerge.push({ positions, indices });
    }

    if (toMerge.length === 0) {
      return {
        positions: new Float32Array(0),
        indices: new Uint32Array(0),
      };
    }

    const [positions, indices] = mergePositionsAndIndices(toMerge);
    return { positions, indices };
  }

  private rasterizeTrianglesWithAreas(
    buildContext: RecastBuildContext,
    heightfield: ReturnType<typeof allocHeightfield>,
    walkableClimb: number,
    positions: Float32Array,
    indices: Uint32Array,
    markWalkableBySlope: boolean,
    walkableSlopeAngle: number,
  ): { success: boolean; error?: string } {
    if (indices.length === 0 || positions.length === 0) {
      return { success: true };
    }

    const numTriangles = indices.length / 3;
    const numVertices = positions.length / 3;
    const verticesArray = new VerticesArray();
    const trianglesArray = new TrianglesArray();
    const triangleAreasArray = new TriangleAreasArray();

    try {
      verticesArray.copy(Array.from(positions));
      trianglesArray.copy(Array.from(indices));
      triangleAreasArray.resize(numTriangles);

      if (markWalkableBySlope) {
        markWalkableTriangles(
          buildContext,
          walkableSlopeAngle,
          verticesArray,
          numVertices,
          trianglesArray,
          numTriangles,
          triangleAreasArray,
        );
      } else {
        for (let i = 0; i < numTriangles; i++) {
          triangleAreasArray.set(i, Recast.RC_NULL_AREA);
        }
      }

      const success = rasterizeTriangles(
        buildContext,
        verticesArray,
        numVertices,
        trianglesArray,
        triangleAreasArray,
        numTriangles,
        heightfield,
        walkableClimb,
      );

      return success ? { success: true } : { success: false, error: 'Could not rasterize triangles' };
    } finally {
      triangleAreasArray.destroy();
      trianglesArray.destroy();
      verticesArray.destroy();
    }
  }

  private createSoloNavMeshFromClassifiedMeshes(
    walkableMeshes: THREE.Mesh[],
    obstacleMeshes: THREE.Mesh[],
    overrideOptions?: any,
  ): { success: boolean; navMesh?: NavMesh; error?: string } {
    const walkableData = this.extractWorldPositionsAndIndices(walkableMeshes);
    const obstacleData = this.extractWorldPositionsAndIndices(obstacleMeshes);

    if (walkableData.indices.length === 0 && obstacleData.indices.length === 0) {
      return { success: false, error: 'No valid mesh geometry found for navmesh generation' };
    }

    const allBoundsPositions = new Float32Array(walkableData.positions.length + obstacleData.positions.length);
    allBoundsPositions.set(walkableData.positions, 0);
    allBoundsPositions.set(obstacleData.positions, walkableData.positions.length);

    const allBoundsIndices = new Uint32Array(walkableData.indices.length + obstacleData.indices.length);
    allBoundsIndices.set(walkableData.indices, 0);
    for (let i = 0; i < obstacleData.indices.length; i++) {
      allBoundsIndices[walkableData.indices.length + i] = obstacleData.indices[i] + (walkableData.positions.length / 3);
    }

    const { bbMin, bbMax } = getBoundingBox(allBoundsPositions, allBoundsIndices);
    const buildContext = new RecastBuildContext();
    const options = { ...this.options, ...(overrideOptions ?? {}) };
    const rcConfig = createRcConfig(options);
    rcConfig.minRegionArea = rcConfig.minRegionArea * rcConfig.minRegionArea;
    rcConfig.mergeRegionArea = rcConfig.mergeRegionArea * rcConfig.mergeRegionArea;
    rcConfig.detailSampleDist = rcConfig.detailSampleDist < 0.9 ? 0 : rcConfig.cs * rcConfig.detailSampleDist;
    rcConfig.detailSampleMaxError = rcConfig.ch * rcConfig.detailSampleMaxError;

    const gridSize = calcGridSize(bbMin, bbMax, rcConfig.cs);
    rcConfig.width = gridSize.width;
    rcConfig.height = gridSize.height;

    const heightfield = allocHeightfield();
    const compactHeightfield = allocCompactHeightfield();
    const contourSet = allocContourSet();
    const polyMesh = allocPolyMesh();
    const polyMeshDetail = allocPolyMeshDetail();

    const cleanup = () => {
      freePolyMeshDetail(polyMeshDetail);
      freePolyMesh(polyMesh);
      freeContourSet(contourSet);
      freeCompactHeightfield(compactHeightfield);
      freeHeightfield(heightfield);
    };

    try {
      if (!createHeightfield(buildContext, heightfield, rcConfig.width, rcConfig.height, bbMin, bbMax, rcConfig.cs, rcConfig.ch)) {
        return { success: false, error: 'Could not create heightfield' };
      }

      const walkableRasterizeResult = this.rasterizeTrianglesWithAreas(
        buildContext,
        heightfield,
        rcConfig.walkableClimb,
        walkableData.positions,
        walkableData.indices,
        true,
        rcConfig.walkableSlopeAngle,
      );
      if (!walkableRasterizeResult.success) {
        return { success: false, error: walkableRasterizeResult.error };
      }

      const obstacleRasterizeResult = this.rasterizeTrianglesWithAreas(
        buildContext,
        heightfield,
        rcConfig.walkableClimb,
        obstacleData.positions,
        obstacleData.indices,
        false,
        rcConfig.walkableSlopeAngle,
      );
      if (!obstacleRasterizeResult.success) {
        return { success: false, error: obstacleRasterizeResult.error };
      }

      filterLowHangingWalkableObstacles(buildContext, rcConfig.walkableClimb, heightfield);
      filterLedgeSpans(buildContext, rcConfig.walkableHeight, rcConfig.walkableClimb, heightfield);
      filterWalkableLowHeightSpans(buildContext, rcConfig.walkableHeight, heightfield);

      if (!buildCompactHeightfield(buildContext, rcConfig.walkableHeight, rcConfig.walkableClimb, heightfield, compactHeightfield)) {
        return { success: false, error: 'Failed to build compact heightfield' };
      }

      if (!erodeWalkableArea(buildContext, rcConfig.walkableRadius, compactHeightfield)) {
        return { success: false, error: 'Failed to erode walkable area' };
      }

      if (!buildDistanceField(buildContext, compactHeightfield)) {
        return { success: false, error: 'Failed to build distance field' };
      }

      if (!buildRegions(buildContext, compactHeightfield, rcConfig.borderSize, rcConfig.minRegionArea, rcConfig.mergeRegionArea)) {
        return { success: false, error: 'Failed to build regions' };
      }

      if (!buildContours(buildContext, compactHeightfield, rcConfig.maxSimplificationError, rcConfig.maxEdgeLen, contourSet, Recast.RC_CONTOUR_TESS_WALL_EDGES)) {
        return { success: false, error: 'Failed to create contours' };
      }

      if (!buildPolyMesh(buildContext, contourSet, rcConfig.maxVertsPerPoly, polyMesh)) {
        return { success: false, error: 'Failed to build poly mesh' };
      }

      if (!buildPolyMeshDetail(buildContext, polyMesh, compactHeightfield, rcConfig.detailSampleDist, rcConfig.detailSampleMaxError, polyMeshDetail)) {
        return { success: false, error: 'Failed to build poly mesh detail' };
      }

      for (let i = 0; i < polyMesh.npolys(); i++) {
        if (polyMesh.areas(i) === Recast.RC_WALKABLE_AREA) {
          polyMesh.setAreas(i, 0);
        }
        if (polyMesh.areas(i) === 0) {
          polyMesh.setFlags(i, 1);
        }
      }

      const navMeshCreateParams = new NavMeshCreateParams();
      navMeshCreateParams.setPolyMeshCreateParams(polyMesh);
      navMeshCreateParams.setPolyMeshDetailCreateParams(polyMeshDetail);
      navMeshCreateParams.setWalkableHeight(rcConfig.walkableHeight * rcConfig.ch);
      navMeshCreateParams.setWalkableRadius(rcConfig.walkableRadius * rcConfig.cs);
      navMeshCreateParams.setWalkableClimb(rcConfig.walkableClimb * rcConfig.ch);
      navMeshCreateParams.setCellSize(rcConfig.cs);
      navMeshCreateParams.setCellHeight(rcConfig.ch);
      navMeshCreateParams.setBuildBvTree(options.buildBvTree ?? true);

      const navMeshDataResult = createNavMeshData(navMeshCreateParams);
      if (!navMeshDataResult.success) {
        return { success: false, error: 'Failed to create navmesh data' };
      }

      const navMesh = new NavMesh();

      if (!navMesh.initSolo(navMeshDataResult.navMeshData)) {
        // initSolo failed — destroy both objects to avoid memory leaks.
        // On success we intentionally do NOT destroy navMeshData: initSolo transfers
        // ownership of the raw tile data to Detour (DT_TILE_FREE_DATA), so destroying
        // it here would free memory that the NavMesh still holds a pointer to, producing
        // a corrupt binary when the mesh is later exported.
        navMeshDataResult.navMeshData.destroy();
        navMesh.destroy();
        return { success: false, error: 'Failed to initialize navmesh from generated data' };
      }

      return { success: true, navMesh };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    } finally {
      cleanup();
    }
  }

  setNavigationMesh(navMesh: any): void {
    if (!navMesh) {
      console.error('[RecastNavigationServer] setNavigationMesh: Navigation mesh not initialized');
      return;
    }

    if (navMesh instanceof NavMesh) {
      // Clean up previous instances to prevent memory leaks
      if (this.navMeshQuery) {
        this.navMeshQuery.destroy();
        this.navMeshQuery = null;
      }
      if (this.navigationMesh) {
        this.navigationMesh.destroy();
        this.navigationMesh = null;
      }

      // Set new navigation mesh and create query instance
      this.navigationMesh = navMesh;
      this.navMeshQuery = new NavMeshQuery(this.navigationMesh);
    } else {
      console.error('[RecastNavigationServer] setNavigationMesh: Navigation mesh is not a valid NavMesh instance');
    }
  }

  /**
   * Clear the current navigation mesh
   */
  clearNavigationMesh(): void {
    console.log('[RecastNavigationServer] clearNavigationMesh: Clearing current navigation mesh');

    // Clean up previous instances to prevent memory leaks
    if (this.navMeshQuery) {
      this.navMeshQuery.destroy();
      this.navMeshQuery = null;
    }
    if (this.navigationMesh) {
      this.navigationMesh.destroy();
      this.navigationMesh = null;
    }
  }

  /**
   * Deinterleaves mesh geometry data only if it actually has interleaved buffer attributes.
   * Uses the proper deinterleaveGeometry utility that checks for InterleavedBufferAttribute.
   * @param meshes Array of THREE.Mesh objects to potentially deinterleave
   * @returns Array of meshes with deinterleaved geometry data where needed
   */
  private deinterleaveMeshes(meshes: THREE.Mesh[]): THREE.Mesh[] {
    return meshes.map(mesh => {
      const clonedGeometry = mesh.geometry.clone();
      deinterleaveGeometry(clonedGeometry);

      // Create a detached mesh so the original scene mesh is never mutated.
      // Copy matrixWorld directly and lock both update flags so that
      // extractWorldPositionsAndIndices' updateMatrixWorld(true) call
      // does not clobber the world transform on the parentless clone.
      const clonedMesh = new THREE.Mesh(clonedGeometry, mesh.material);
      clonedMesh.matrixWorld.copy(mesh.matrixWorld);
      clonedMesh.matrixAutoUpdate = false;
      clonedMesh.matrixWorldAutoUpdate = false;
      return clonedMesh;
    });
  }

  calculatePath(start: THREE.Vector3, end: THREE.Vector3): THREE.Vector3[] {
    if (!this.navigationMesh || !this.navMeshQuery) {
      console.error('[RecastNavigationServer] calculatePath: Navigation mesh not initialized');
      return [];
    }

    const startInfo = this.navMeshQuery.findNearestPoly(start);
    const endInfo = this.navMeshQuery.findNearestPoly(end);

    const pathResult = this.navMeshQuery.computePath(
      startInfo.nearestPoint,
      endInfo.nearestPoint,
    );

    return pathResult.path.map(point => new THREE.Vector3(point.x, point.y, point.z));
  }

  /**
   * Checks if a point is on the navigation mesh
   * @param point The point to check
   * @returns True if the point is on the navigation mesh, false otherwise
   */
  isPointOnNavigationMesh(point: THREE.Vector3): boolean {
    if (!this.navigationMesh || !this.navMeshQuery) {
      console.error('[RecastNavigationServer] isPointOnNavigationMesh: Navigation mesh not initialized');
      return false;
    }

    const result = this.navMeshQuery.findNearestPoly(point);

    // Check if a valid polygon reference was found
    // and if the point is directly over that polygon (isOverPoly property)
    if (result.nearestRef && result.isOverPoly) {
      return true;
    }

    return false;
  }

  /**
   * Gets the closest point on the navigation mesh to the specified point
   * @param point The reference point
   * @returns The closest point on the navigation mesh, or the original point if the navigation mesh is not initialized
   */
  getClosestPointOnNavigationMesh(point: THREE.Vector3): THREE.Vector3 {
    if (!this.navigationMesh || !this.navMeshQuery) {
      console.error('[RecastNavigationServer] getClosestPointOnNavigationMesh: Navigation mesh not initialized');
      return point.clone();
    }

    const result = this.navMeshQuery.findNearestPoly(point);

    if (result.nearestPoint) {
      return new THREE.Vector3(
        result.nearestPoint.x,
        result.nearestPoint.y,
        result.nearestPoint.z
      );
    }

    // If no nearest point could be found, return the original point
    return point.clone();
  }

  /**
   * Create debug visualization for the navigation mesh
   * @returns A DebugDrawer instance that can be added to the scene, or null if no navigation mesh exists
   */
  createDebugVisualization(): THREE.Group | null {
    if (!this.navigationMesh) {
      console.warn('[RecastNavigationServer] createDebugVisualization: No navigation mesh available');
      return null;
    }

    try {
      const debugDrawer = new DebugDrawer();
      debugDrawer.drawNavMesh(this.navigationMesh, 0);
      replaceDebugDrawerLinesWithThreeEdges(debugDrawer);
      return debugDrawer;
    } catch (error) {
      console.error('[RecastNavigationServer] createDebugVisualization: Failed to create debug visualization:', error);
      return null;
    }
  }

  /**
   * Export the current navigation mesh to a binary file
   * @param scenePath The path to the scene file (used to determine navmesh file path)
   * @returns Promise resolving to export result with data and filename
   */
  async exportNavigationMesh(scenePath: AssetPath): Promise<{ success: boolean; data?: ArrayBuffer; filename?: string }> {
    if (!this.navigationMesh) {
      console.error('[RecastNavigationServer] exportNavigationMesh: No navigation mesh available');
      return { success: false };
    }

    try {
      // Use the proper recast-navigation exportNavMesh function
      const navMeshData = exportNavMesh(this.navigationMesh);

      console.log('[RecastNavigationServer] exportNavigationMesh: NavMesh serialized');
      console.log('  Data size:', navMeshData.length, 'bytes');

      // Determine filename - basing it off the scene path minus the scene extension
      const filename = `${scenePath.initialPath.split('/').pop()?.replace(/\.[^.]+$/, '') ?? 'navmesh'}${NAVMESH_EXT}`;

      // Convert Uint8Array to ArrayBuffer for consistency with other export APIs
      // Ensure we return ArrayBuffer, not SharedArrayBuffer by creating a new buffer
      const arrayBuffer = new ArrayBuffer(navMeshData.length);
      new Uint8Array(arrayBuffer).set(navMeshData);

      console.log(`[RecastNavigationServer] exportNavigationMesh: NavMesh data prepared for export as ${filename}`);
      return { success: true, data: arrayBuffer, filename };

    } catch (error) {
      console.error('[RecastNavigationServer] exportNavigationMesh: Export failed:', error);
      return { success: false };
    }
  }

  /**
   * Import a navigation mesh from a binary file
   * @param scenePath The path to the scene file (used to determine navmesh file path)
   * @returns Promise resolving to success/failure
   */
  async importNavigationMesh(scenePath: AssetPath): Promise<boolean> {
    try {
      // Determine input path basing it off the scene path minus the scene extension
      const pathWithoutExt = scenePath.initialPath.replace(/\.[^.]+$/, '');
      const navmeshPath = `${pathWithoutExt}${NAVMESH_EXT}`;

      // Load the NavMesh file
      const provider = projectInfo.storageProvider;
      if (!provider) {
        console.error('[RecastNavigationServer] importNavigationMesh: No storage provider available');
        return false;
      }

      const navMeshBuffer = await provider.downloadFileAsBuffer(new AssetPath(navmeshPath));

      // Use the data-based import method
      return await this.importNavigationMeshFromData(navMeshBuffer);
    } catch (error) {
      console.error('[RecastNavigationServer] importNavigationMesh: Import failed:', error);
      return false;
    }
  }

  /**
   * Import a navigation mesh from NavMesh data directly
   * @param navMeshData The NavMesh data as ArrayBuffer (from recast-navigation export)
   * @returns Promise resolving to success/failure
   */
  async importNavigationMeshFromData(navMeshData: ArrayBuffer): Promise<boolean> {
    try {
      // Convert ArrayBuffer to Uint8Array for recast-navigation
      const uint8Array = new Uint8Array(navMeshData);

      console.log('[RecastNavigationServer] importNavigationMeshFromData: NavMesh data received');
      console.log('  Data size:', uint8Array.length, 'bytes');

      // Use the proper recast-navigation importNavMesh function
      const result = importNavMesh(uint8Array);

      if (!result || !result.navMesh) {
        console.error('[RecastNavigationServer] importNavigationMeshFromData: Failed to import navigation mesh');
        console.error('  importNavMesh result:', result);
        return false;
      }

      // Set the new navigation mesh
      // using setNavigationMesh wires NavMeshQuery (required for path queries / point tests)
      this.setNavigationMesh(result.navMesh);
      console.log('[RecastNavigationServer] Navigation mesh imported from data successfully');
      return true;

    } catch (error) {
      console.error('[RecastNavigationServer] importNavigationMeshFromData: Import failed:', error);
      return false;
    }
  }

  isReady(): boolean {
    return this.navigationMesh !== null;
  }
}